import { Flex, Text } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, FormFeedback, Modal } from '@morressier/alembic';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import z from 'zod';
import MultiSelect, { Option } from '../../../../../components/MultiSelect';

interface Props {
  onClose: () => void;
  onSubmit: (rounds: IAddReminderCycleFormType) => void;
  isOpen: boolean;
  existingRoundDays?: number[];
}

export type IAddReminderCycleFormType = {
  reminderDays: string[];
};

const reminderDaysSchema = z.object({
  reminderDays: z.array(z.string()).min(1, {
    message: 'Select at least 1 option'
  })
});

const reminderCycleDaysRange = Array.from({ length: 31 }, (_, i) => i - 15);

const options: Option[] = reminderCycleDaysRange.map(n => ({
  value: n.toString(),
  label: `${n > 0 ? '+' : ''}${n} day${
    n === 1 || n === -1 || n === 0 ? '' : 's'
  }`
}));

const AddReminderCycleModal = ({
  onClose,
  onSubmit,
  isOpen,
  existingRoundDays
}: Props) => {
  const methods = useForm<IAddReminderCycleFormType>({
    defaultValues: {
      reminderDays: existingRoundDays?.map(day => day.toString()) ?? []
    },
    resolver: zodResolver(reminderDaysSchema)
  });

  const {
    handleSubmit,
    formState: { errors },
    control
  } = methods;

  const submitData = (rounds: IAddReminderCycleFormType) => {
    onSubmit(rounds);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width={{ base: 'calc(100vw - 16px)', md: '45rem' } as any}
      closeButtonProps={{
        hidden: true
      }}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(submitData)}>
          <Flex flexDirection="column" gap={6} padding={{ base: 4, md: 8 }}>
            <Flex flexDirection="column" gap={2}>
              <Text textStyle="headingGroup">Add New Cycle</Text>
              <Text textStyle="textBody">
                Customize your Reminder Cycle by selecting multiple days from
                the options below to appear in the 'Reminder cycle' field.
              </Text>
            </Flex>
            <Flex flexDirection="column" gap={2}>
              <Text textStyle="headingGroup">Select Days</Text>
              <Controller
                control={control}
                name="reminderDays"
                render={({ field }) => (
                  <MultiSelect
                    options={options}
                    value={field.value.map(value => value?.toString()) || []}
                    onChange={field.onChange}
                    placeholder="Select"
                    variant="interactive"
                    hideClearButton
                  />
                )}
              />
              {errors.reminderDays && (
                <FormFeedback variant="error">
                  {errors.reminderDays.message as string}
                </FormFeedback>
              )}
            </Flex>
            <Flex width="100%" justifyContent="flex-end" gap={4}>
              <Button variant="secondary" onClick={onClose} isDisabled={false}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" isDisabled={false}>
                Save
              </Button>
            </Flex>
          </Flex>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default AddReminderCycleModal;
